import React, { useMemo, useState } from 'react';
import { Users, X, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * computeGroupName — derive a full "customer group name" from a list
 * of matched customer objects. Used by the "Select all matching KEYWORD"
 * bulk action to send a clean, uppercased group label (e.g.
 * "MAKKAH HYPERMARKET") to the PDF filter summary instead of the raw
 * abbreviated search text the user typed ("Makkah hy").
 *
 * Algorithm: longest common leading token sequence across the matched
 * `name` fields (case-insensitive, uppercased). If the matches share
 * no common leading token (e.g. a substring hit that lands mid-name),
 * falls back to the raw keyword uppercased so the header never blanks
 * out.
 *
 * Examples:
 *   ["MAKKAH HYPERMARKET SEEB", "MAKKAH HYPERMARKET SALALAH"]
 *     → "MAKKAH HYPERMARKET"
 *   ["LULU BAUSHAR", "LULU HYPERMARKET SALALAH"]
 *     → "LULU"
 *   [] with fallback "hy" → "HY"
 */
export const computeGroupName = (items, fallbackKeyword = '') => {
  const fallback = String(fallbackKeyword || '').trim().toUpperCase();
  const names = (items || [])
    .map((o) => String((o && (o.name || o.customer_name)) || '').trim().toUpperCase())
    .filter(Boolean);
  if (names.length === 0) return fallback;
  if (names.length === 1) return names[0];
  // Tokenize on whitespace (drop punctuation-only tokens).
  const tokenLists = names.map((n) => n.split(/\s+/).filter(Boolean));
  const minLen = Math.min(...tokenLists.map((l) => l.length));
  const common = [];
  for (let i = 0; i < minLen; i++) {
    const first = tokenLists[0][i];
    if (tokenLists.every((l) => l[i] === first)) {
      common.push(first);
    } else {
      break;
    }
  }
  return common.length > 0 ? common.join(' ') : fallback;
};

/**
 * CustomerMultiSelect — searchable, multi-select customer picker.
 *
 * Usage identical on the admin dashboard AND the public share-link
 * view: pass the full customer list (`options: [{code, name, ...}]`),
 * plus the current selection state (`value: string[] of customer_code`)
 * and a change handler.
 *
 * Search matches BOTH the `customer_code` and the `name`
 * (case-insensitive substring) — same predicate the free-text search
 * uses so admins get consistent results.
 *
 * Renders a compact trigger button (icon + count summary + chevron)
 * that opens a 320-wide popover with a search input, checkbox rows,
 * and a "Clear all" affordance. Selected rows show inline as blue
 * pills above the trigger so users can revoke a single customer
 * without re-opening the picker.
 */
export const CustomerMultiSelect = ({
  options,
  value,
  onChange,
  label = 'Customers',
  placeholder = 'Search code or name…',
  testId = 'customer-multi-select',
  className = '',
  // Feb 2026 (July) — optional keyword provenance callback so parents
  // can track when the "Select all matching KEYWORD" bulk action was
  // used to build the selection. The parent gets the keyword after
  // bulk-select, or an empty string when the user manually toggles /
  // clears (which invalidates the group provenance).
  onKeywordChange,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const selectedCount = selectedSet.size;

  // Normalise a string for hyphen/special-char-tolerant matching.
  // Lower-cases, strips diacritics, and collapses every non-alphanumeric
  // character (`-`, `.`, `&`, `,`, `'`, em-dashes, whitespace runs, …)
  // into a single space. Applied symmetrically on the query AND the
  // code/name being searched so "FDSULTAN - THE SULTAN",
  // "fdsultan  the sultan", "FDSULTAN-THE" all normalise to
  // "fdsultan the sultan" and match consistently.
  const norm = (s) => (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  // Filter + rank: exact code matches first, then name-prefix, then substring.
  // Search is hyphen/whitespace tolerant AND multi-token AND (Feb 2026 fix):
  //   * Every whitespace-separated token in the query must appear as a
  //     substring in the (normalised) code+name blob, so "sultan water"
  //     narrows further rather than widening.
  //   * The 300-item cap now applies ONLY to the "no query" default
  //     view — once the user is actively filtering, ALL matches are
  //     surfaced so no customer is silently missing regardless of
  //     division or list length.
  const filtered = useMemo(() => {
    const qRaw = (q || '').trim();
    const arr = options || [];
    if (!qRaw) return arr.slice(0, 300);
    const qq = norm(qRaw);
    const tokens = qq ? qq.split(' ').filter(Boolean) : [];
    const codeExact = [];
    const codePrefix = [];
    const namePrefix = [];
    const subMatch = [];
    for (const o of arr) {
      const code = (o.customer_code || o.code || '').toString();
      const name = (o.name || '').toString();
      const c = norm(code);
      const n = norm(name);
      const blob = c + ' ' + n;
      // AND across tokens — every token must appear somewhere.
      if (tokens.length && !tokens.every((t) => blob.includes(t))) continue;
      if (c === qq) codeExact.push(o);
      else if (c.startsWith(qq)) codePrefix.push(o);
      else if (n.startsWith(qq)) namePrefix.push(o);
      else subMatch.push(o);
    }
    return [...codeExact, ...codePrefix, ...namePrefix, ...subMatch];
  }, [options, q]);

  const toggle = (code) => {
    const next = new Set(selectedSet);
    if (next.has(code)) next.delete(code); else next.add(code);
    // Manual toggle → invalidate keyword-group provenance so the
    // exported PDF's filter summary falls back to a plain count.
    if (onKeywordChange) onKeywordChange('');
    onChange(Array.from(next));
  };
  const clearAll = () => {
    if (onKeywordChange) onKeywordChange('');
    onChange([]);
  };

  // Feb 2026 (bulk-select-matching): when the user has typed a query
  // AND ≥ 2 filtered entries are on screen, offer a one-tap "Select
  // all matching" action that toggles the ENTIRE filtered set at
  // once. If every filtered entry is already selected, the action
  // becomes "Deselect all matching" so a user who over-selected can
  // pull back with the same tap. `Q_LABEL` is capped so a long query
  // doesn't stretch the row off the popover.
  const trimmedQ = (q || '').trim();
  const bulkEligible = trimmedQ.length > 0 && filtered.length >= 2;
  const allMatchingSelected = bulkEligible && filtered.every((o) => selectedSet.has(o.customer_code || o.code));
  const bulkLabel = trimmedQ.length > 24 ? `${trimmedQ.slice(0, 24)}…` : trimmedQ;
  const toggleAllMatching = () => {
    const next = new Set(selectedSet);
    const codes = filtered.map((o) => o.customer_code || o.code);
    if (allMatchingSelected) {
      codes.forEach((c) => next.delete(c));
      if (onKeywordChange) onKeywordChange('');
    } else {
      codes.forEach((c) => next.add(c));
      // Track the FULL customer group name (not the abbreviated
      // search query) so the exported PDF filter summary reads
      // `MAKKAH HYPERMARKET (60)` instead of `Makkah hy (60)`.
      // We derive it from the longest common leading token sequence
      // of the matched customer names — that's typically the shared
      // brand root ("MAKKAH HYPERMARKET" across multiple branches).
      // Falls back to the raw keyword uppercased when the matches
      // don't share a leading prefix.
      const groupName = computeGroupName(filtered, trimmedQ);
      if (onKeywordChange) onKeywordChange(groupName);
    }
    onChange(Array.from(next));
  };

  const summary = selectedCount === 0
    ? `All ${label.toLowerCase()}`
    : selectedCount === 1
      ? (() => {
          const one = (options || []).find((o) => (o.customer_code || o.code) === Array.from(selectedSet)[0]);
          return one ? `${one.customer_code || one.code} · ${one.name}` : Array.from(selectedSet)[0];
        })()
      : `${selectedCount} customers`;

  // Grab full option info by code for the selected pills.
  const selectedOptions = useMemo(() => {
    const byCode = new Map((options || []).map((o) => [(o.customer_code || o.code), o]));
    return (value || []).map((c) => byCode.get(c) || { customer_code: c, name: '' });
  }, [options, value]);

  return (
    <div className={`flex flex-col gap-1.5 min-w-[240px] ${className}`} data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`h-10 w-full inline-flex items-center gap-2 px-3 rounded-md border ${
              selectedCount > 0 ? 'border-[#0078D4] bg-[#0078D4]/5' : 'border-neutral-300 bg-white'
            } text-sm text-left transition-colors hover:border-neutral-400`}
            data-testid={`${testId}-trigger`}
          >
            <Users className="w-4 h-4 text-neutral-500 flex-shrink-0" />
            <span className={`flex-1 truncate ${selectedCount > 0 ? 'font-semibold text-[#0F172A]' : 'text-neutral-500'}`}>
              {summary}
            </span>
            <ChevronDown className="w-4 h-4 text-neutral-400 flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[380px] max-w-[calc(100vw-24px)] p-0" data-testid={`${testId}-popover`}>
          <div className="p-2 border-b border-neutral-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-neutral-400" />
              <input
                type="text"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={placeholder}
                className="w-full h-9 pl-8 pr-2 rounded-md border border-neutral-200 text-sm focus:outline-none focus:border-[#0078D4]"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-1" data-testid={`${testId}-list`}>
            {bulkEligible && (
              <button
                type="button"
                onClick={toggleAllMatching}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded text-sm mb-1 border-b border-neutral-100 sticky top-0 z-10 ${
                  allMatchingSelected
                    ? 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                    : 'bg-neutral-50 text-neutral-800 hover:bg-blue-50'
                } font-semibold text-left transition-colors`}
                data-testid={`${testId}-select-all-matching`}
              >
                <span className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border ${allMatchingSelected ? 'bg-[#0078D4] border-[#0078D4]' : 'border-neutral-300 bg-white'}`}>
                  {allMatchingSelected && (
                    <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="2.5,6.5 5,9 9.5,3.5" />
                    </svg>
                  )}
                </span>
                <span className="flex-1">
                  {allMatchingSelected ? 'Deselect all' : 'Select all'}: &ldquo;{bulkLabel}&rdquo;
                </span>
                <span className="text-[11px] font-mono font-bold bg-white/60 px-1.5 py-0.5 rounded-full">
                  {filtered.length}
                </span>
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-neutral-400 italic">
                No customer matches &quot;{q}&quot;.
              </div>
            ) : (
              filtered.map((o) => {
                const code = o.customer_code || o.code;
                const isChecked = selectedSet.has(code);
                return (
                  <label
                    key={code}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-neutral-50 ${isChecked ? 'bg-blue-50/60' : ''}`}
                    data-testid={`${testId}-option-${code}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(code)}
                      className="w-4 h-4 accent-[#0078D4] flex-shrink-0"
                    />
                    <span className="font-mono text-[11px] font-bold text-neutral-500 min-w-[60px]">{code}</span>
                    <span className="flex-1 truncate text-neutral-800">{o.name || <em className="text-neutral-400">(no name)</em>}</span>
                  </label>
                );
              })
            )}
          </div>
          {selectedCount > 0 && (
            <div className="p-2 border-t border-neutral-200 flex items-center justify-between">
              <span className="text-xs text-neutral-600">{selectedCount} selected</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs font-semibold text-red-600 hover:text-red-800"
                data-testid={`${testId}-clear`}
              >
                Clear all
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {selectedCount > 0 && selectedCount <= 8 && (
        <div className="flex gap-1 flex-wrap" data-testid={`${testId}-pills`}>
          {selectedOptions.map((o) => (
            <span
              key={o.customer_code || o.code}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[11px] font-semibold border border-blue-200"
            >
              <span className="font-mono">{o.customer_code || o.code}</span>
              <button
                type="button"
                onClick={() => toggle(o.customer_code || o.code)}
                className="hover:bg-blue-200 rounded-full p-0.5"
                aria-label={`Remove ${o.customer_code || o.code}`}
                data-testid={`${testId}-pill-remove-${o.customer_code || o.code}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerMultiSelect;
